import React, { useEffect, useState } from "react";
import {
  CheckCircleOutlined,
  FileSearchOutlined,
  PicCenterOutlined,
  FieldTimeOutlined,
  UserOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Select, Carousel } from 'antd';
import classNames from 'classnames';
import { useDispatch, useSelector } from "react-redux";

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';
import SectionMission from "../Sections/SectionMission";
import SectionWhy from "../Sections/SectionWhy";
import SectionChoice from "../Sections/SectionChoice";
import SectionPartners from "../Sections/SectionPartners";
import SectionCTA from "../Sections/SectionCTA";
import SectionBlogs from "../Sections/SectionBlogs";
import { withViewport } from "../../../../util/uiHelpers";
import { useIntl } from "react-intl";
import { createResourceLocatorString } from "../../../../util/routes";
import { useHistory, useLocation } from "react-router-dom";
import { useRouteConfiguration } from "../../../../context/routeConfigurationContext";
import { fetchSectionsData } from "../../../../ducks/strapi.duck";
import { parse } from "../../../../util/urlHelpers";
import partners from './img/partners.jpg';
import rfps from './img/rfps.jpg';
import talent from './img/talent.jpg';
import work from './img/work.jpg';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options
  } = props;

  const intl = useIntl();
  const history = useHistory();
  const currentLocation = useLocation();
  const routeConfiguration = useRouteConfiguration();

  const search = parse(currentLocation.search);
  const dispatch = useDispatch();

  const sectionsData = useSelector(state => state.strapi?.sectionsData) || [];

  // Fetch sections data from Strapi
  useEffect(() => {
    dispatch(fetchSectionsData());
  }, [dispatch, search?.tag]);

  // Get section data for each category
  const talentSection = sectionsData?.sections?.filter(sec => sec?.heading === 'Talent Banner')[0] || {};
  const workSection = sectionsData?.sections?.filter(sec => sec?.heading === 'Work Banner')[0] || {};
  const partnersSection = sectionsData?.sections?.filter(sec => sec?.heading === 'Partners Banner')[0] || {};
  const rfpsSection = sectionsData?.sections?.filter(sec => sec?.heading === 'RFPs Banner')[0] || {};

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const [keyword, setKeyword] = useState(null);
  const [listingType, setListingType] = useState(null);

  const handleKeywordChange = (value) => {
    setKeyword(value?.currentTarget?.value == '' ? null : value?.currentTarget?.value);
  };

  const handleSelectChange = (value) => {
    setListingType(value);
  };

  const redirectToSearchPage = (listingTypeParam) => {
    if (listingTypeParam == 'search') {
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, { pub_listingType: listingType, keywords: keyword }));
    } else {
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, { pub_listingType: listingTypeParam }));
    }
  };

  return (
    <>
      <div className={css.heroAccordion}>
        <ul className={css.iAccordion}>
          <li className={css.iItem}
            style={{ backgroundImage: `url(${talent})` }}
          >
            <div className={css.iAction}>
              <div className={css.iContent}>
                <h2 className={css.iTitle}>
                  {talentSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {talentSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < talentSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {talentSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Hire Fast <em></em> Hire Smart <em></em> Hire Verified.</span><br />
                      Access pre-verified IT talent - identity and work authorization document integrity verified upfront, with experience verification coming soon. Cut hiring time, cost, and fraud from day one.
                    </>
                  )}
                </h2>

                <div className={css.iBtns}>
                  <Button size="large" href="#talent">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('candidate')}>Find Talent</Button>
                </div>
              </div>
              <div className={css.iAsie}>
                <h2 className={css.iHeading}>Talent</h2>
              </div>
            </div>
          </li>
          <li className={css.iItem}
            style={{ backgroundImage: `url(${work})` }}
          >
            <div className={css.iAction}>
              <div className={css.iContent}>
                <h2 className={css.iTitle}>
                  {workSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {workSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < workSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {workSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Get Hired Faster <em></em> Earn More <em></em> Prove You're Real.</span><br />
                      Show you're verified. Get noticed faster. Control your credentials and boost your earnings.
                    </>
                  )}
                </h2>
                <div className={css.iBtns}>
                  <Button size="large" href="#work">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('job_listing')}>Find Work</Button>
                </div>
              </div>
              <div className={css.iAsie}>
                <h2 className={css.iHeading}>Work</h2>
              </div>
            </div>
          </li>
          <li className={css.iItem}
            style={{ backgroundImage: `url(${partners})` }}
          >
            <div className={css.iAction}>
              <div className={css.iContent}>
                <h2 className={css.iTitle}>
                  {partnersSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {partnersSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < partnersSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {partnersSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Discover Your Next Best Supplier—Not Just the Usual Suspects.</span><br />
                      Explore a dynamic, verified supplier list beyond the big firms. Compare capabilities, not just names.
                    </>
                  )}
                </h2>
                <div className={css.iBtns}>
                  <Button size="large" href="#partners">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('agency')}>Find Implementation Suppliers</Button>
                </div>
              </div>
              <div className={css.iAsie}>
                <h2 className={css.iHeading}>Suppliers</h2>
              </div>
            </div>
          </li >
          <li className={css.iItem}
            style={{ backgroundImage: `url(${rfps})` }}
          >
            <div className={css.iAction}>
              <div className={css.iContent}>
                <h2 className={css.iTitle}>
                  {rfpsSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {rfpsSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < rfpsSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {rfpsSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Break In <em></em> Win More <em></em> Compete Where Big Firms Can't Block You.</span><br />
                      Access verified RFPs in a fair, open marketplace that rewards capabilities—not brand name.
                    </>
                  )}
                </h2>
                <div className={css.iBtns}>
                  <Button size="large" href="#rpfs">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('rfp_listing')}>Find RFPs</Button>
                </div>
              </div>
              <div className={css.iAsie}>
                <h2 className={css.iHeading}>RFPs</h2>
              </div>
            </div>
          </li>
        </ul>
        <div className={css.iSearch}>
          <div className={css.iSearchContainer}>
            <Form
              autoComplete="off"
            >
              <Form.Item>
                <Input id="searchInput" placeholder={"Search listings…"} onChange={handleKeywordChange} />
              </Form.Item>
              <Form.Item>
                <Select id="searchSelect" onChange={handleSelectChange} placeholder="Select Category" className={css.selectBox}>
                  <Select.Option value="candidate">Talent</Select.Option>
                  <Select.Option value="job_listing">Work</Select.Option>
                  <Select.Option value="agency">Suppliers</Select.Option>
                  <Select.Option value="rfp_listing">RFPs</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item>
                <Button type="primary" size="large" icon={<SearchOutlined />} onClick={() => redirectToSearchPage('search')}>
                  Search
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div >

      <div className={css.heroCarousel}>
        <Carousel arrows dots={false}>
          <div className={css.cItem}>
            <div className={css.cImg} style={{ backgroundImage: `url(${talent})` }}>
              <div className={css.cContent}>
                {/* <h6>Talent</h6> */}
                <h5 className={css.iTitle}>
                  {talentSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {talentSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < talentSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {talentSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Hire Fast <em></em> Hire Smart <em></em> Hire Verified.</span><br />
                      Access pre-verified IT talent - identity and work authorization document integrity verified upfront, with experience verification coming soon. Cut hiring time, cost, and fraud from day one.
                    </>
                  )}
                </h5>
                <div className={css.iBtns}>
                  <Button size="large" href="#talent">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('candidate')}>Find Talent</Button>
                </div>
              </div>
              <div className={css.cOverlay}></div>
            </div>
          </div>
          <div className={css.cItem}>
            <div className={css.cImg} style={{ backgroundImage: `url(${work})` }}>
              <div className={css.cContent}>
                {/* <h6>Work</h6> */}
                <h5 className={css.iTitle}>
                  {workSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {workSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < workSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {workSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Get Hired Faster <em></em> Earn More <em></em> Prove You're Real.</span><br />
                      Show you're verified. Get noticed faster. Control your credentials and boost your earnings.
                    </>
                  )}
                </h5>
                <div className={css.iBtns}>
                  <Button size="large" href="#work">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('job_listing')}>Find Work</Button>
                </div>
              </div>
              <div className={css.cOverlay}></div>
            </div>
          </div>
          <div className={css.cItem}>
            <div className={css.cImg} style={{ backgroundImage: `url(${partners})` }}>
              <div className={css.cContent}>
                {/* <h6>Partners</h6> */}
                <h5 className={css.iTitle}>
                  {partnersSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {partnersSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < partnersSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {partnersSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Discover Your Next Best Supplier—Not Just the Usual Suspects.</span><br />
                      Explore a dynamic, verified supplier list beyond the big firms. Compare capabilities, not just names.
                    </>
                  )}
                </h5>
                <div className={css.iBtns}>
                  <Button size="large" href="#partners">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('agency')}>Find Suppliers</Button>
                </div>
              </div>
              <div className={css.cOverlay}></div>
            </div>
          </div>
          <div className={css.cItem}>
            <div className={css.cImg} style={{ backgroundImage: `url(${rfps})` }}>
              <div className={css.cContent}>
                {/* <h6>RFPs</h6> */}
                <h5 className={css.iTitle}>
                  {rfpsSection?.data?.length > 0 ? (
                    <>
                      <span>
                        {rfpsSection.data.map((item, index) => (
                          <React.Fragment key={item.id}>
                            {item.title}
                            {index < rfpsSection.data.length - 1 && <em></em>}
                          </React.Fragment>
                        ))}
                      </span>
                      <br />
                      {rfpsSection?.textBelowHeading}
                    </>
                  ) : (
                    <>
                      <span>Break In <em></em> Win More <em></em> Compete Where Big Firms Can't Block You.</span><br />
                      Access verified RFPs in a fair, open marketplace that rewards capabilities—not brand name.
                    </>
                  )}
                </h5>
                <div className={css.iBtns}>
                  <Button size="large" href="#rpfs">Learn More</Button>
                  <Button type="primary" size="large" onClick={() => redirectToSearchPage('rfp_listing')}>Find RFPs</Button>
                </div>
              </div>
              <div className={css.cOverlay}></div>
            </div>
          </div>
        </Carousel>
      </div>
      {/* <SectionMission viewport={viewport} intl={intl} />
          <SectionWhy viewport={viewport} intl={intl} />
          <SectionChoice viewport={viewport} intl={intl} />
          <SectionPartners viewport={viewport} />
          <SectionCTA viewport={viewport} />
          <SectionBlogs viewport={viewport} /> */}


      <SectionMission history={history} routeConfiguration={routeConfiguration} />
      <SectionWhy history={history} routeConfiguration={routeConfiguration} />
      <SectionChoice history={history} routeConfiguration={routeConfiguration} />
      <SectionPartners history={history} routeConfiguration={routeConfiguration} />
      <SectionCTA history={history} routeConfiguration={routeConfiguration} />
      <SectionBlogs history={history} routeConfiguration={routeConfiguration} />

    </>
  );
};

export default SectionHero;
